import { z } from 'zod';

// Entity type enum schema
export const EntityTypeSchema = z.enum([
  // Platform-level entities (globally unique)
  'tenants',
  'restaurants', // scoped-path-ok: counter entity key (stored doc id)
  'domain_mappings', // @deprecated - use 'domains' for unified domain management
  'domains',
  'integrations',
  'system_roles',
  'allergy_categories',
  'ingredients',
  // Restaurant-level entities (unique within restaurant)
  'items',
  'courses',
  'sales_categories',
  'tax_rates',
  'modifier_groups',
  'modifiers',
  'menu_groups',
  'tags',
  'prep_stations',
  'printers',
  // A cash drawer is its OWN entity (CD-0), not an array element on a printer.
  // `ENTITY_ID_RANGES.cash_drawers` landed in @order-up/models without a
  // matching member here, so `validateEntityType('cash_drawers')` →
  // `EntityTypeSchema.parse` threw a 400 VALIDATION_ERROR and NO drawer could
  // ever be created. Same drift shape as the scheduling aggregates below;
  // `counterEntityTypeParity.test.ts` is the seam that catches it.
  'cash_drawers',
  // Kitchen buzzer — a FIRST-CLASS ENTITY in `kitchen_buzzers/{guid}` (KB-1),
  // sitting beside `cash_drawers` because both share the same DK-port budget.
  // Kept in sync with @order-up/models ENTITY_ID_RANGES for the same reason
  // as `cash_drawers` above — omission here is a 400 on every buzzer create.
  'kitchen_buzzers',
  'employees',
  'shifts',
  'shift_tasks',
  'job_titles',
  'departments',
  // Scheduling-aggregate entities. Present in the models `EntityType` union +
  // ENTITY_ID_RANGES, but were missing from this Zod enum, so
  // `EntityTypeSchema.parse('schedules'|'shift_requests'|'labor_warnings')` in
  // counter generation threw a 400 VALIDATION_ERROR. Kept in sync with
  // @order-up/models EntityType.
  'schedules',
  'shift_requests',
  'labor_warnings',
  'orders',
  'checks',
  // POS RTDB-counter entities — also part of the models EntityType union; kept
  // here so the Zod enum matches the type exactly.
  'time_entries',
  'transfer_records',
  'carts',
  'reviews',
  'media',
  'table_layouts',
  'tables',
  'revenue_centers',
  'table_assignments',
  'table_merges',
  'waitlist_entries',
  'seated_customers',
  'reservations',
  'devices',
  'notifications',
  'platform_requests',
  'permission_groups',
  'permission_sub_accesses',
  'job_permissions',
  'discounts',
  'void_reasons',
  'payments',
  'support_sessions',
  'gift_cards',
  'gift_card_transactions',
  'business_types',
  'business_sub_categories',
  'network_configs',
  // Appointments (appointment-style scheduling) — restaurant-level entities.
  // Kept in sync with @order-up/models EntityType + ENTITY_ID_RANGES
  // (counterEntityTypeParity.test.ts is the seam that enforces this).
  'bookable_services',
  'appointments',
]);

// Schema for the Counter model
const CounterSchema = z.object({
  entityType: EntityTypeSchema.readonly(),
  nextId: z.number().int().min(0).default(0).readonly(),
  createdAt: z.date().optional().readonly(),
  updatedAt: z.date().optional().readonly(),
}).strict();

// Allow timestamps in create (they won't be auto-added by createWithId)
export const CreateCounterSchema = CounterSchema.omit({});

// Allow updatedAt in updates, but not entityType or createdAt
export const UpdateCounterSchema = CounterSchema.partial().omit({
  entityType: true, // entityType should not be updatable
  createdAt: true,  // createdAt cannot be changed after creation
});

// TypeScript type inferred from CounterSchema
export type CounterType = z.infer<typeof CounterSchema>;
export type CreateCounterType = z.infer<typeof CreateCounterSchema>;
export type UpdateCounterType = z.infer<typeof UpdateCounterSchema>;
export type EntityType = z.infer<typeof EntityTypeSchema>;

export default CounterSchema;
